import { useState, useRef, useEffect, KeyboardEvent } from "react";
import { X, Send } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChatMessage } from "./ChatMessage";
import { ChatTypingIndicator } from "./ChatTypingIndicator";
import { useToast } from "@/hooks/use-toast";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface ConversationState {
  leadId?: string;
  leadCreated: boolean;
  meetingScheduled: boolean;
  collectedData: {
    nombre?: string;
    telefono?: string;
    email?: string;
    renta_mensual?: number;
    objetivo?: string;
  };
}

interface ChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const WELCOME_MESSAGE = "Hola, soy Florencia de REA 👋 Podemos encontrar una excelente oportunidad para tu inversión. Para asesorarte mejor, necesito algunos datos rápidos. ¿Buscas invertir o vivir?";

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/florencia-chat`;

export const ChatPanel = ({ isOpen, onClose }: ChatPanelProps) => {
  const [messages, setMessages] = useState<Message[]>([
    { role: "assistant", content: WELCOME_MESSAGE }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversationState, setConversationState] = useState<ConversationState>({
    leadCreated: false,
    meetingScheduled: false,
    collectedData: {}
  });
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { toast } = useToast();

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  // Focus textarea when panel opens
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 300);
    }
  }, [isOpen]);

  const sendMessage = async () => {
    const trimmedInput = input.trim();
    if (!trimmedInput || isLoading) return;

    // Limit input length
    if (trimmedInput.length > 1000) {
      toast({
        title: "Mensaje muy largo",
        description: "Por favor, escribe un mensaje más corto.",
        variant: "destructive"
      });
      return;
    }

    const userMessage: Message = { role: "user", content: trimmedInput };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setIsLoading(true);

    try {
      // Send only last 16 messages for context (8 exchanges)
      const messagesToSend = newMessages.slice(-16).map(m => ({
        role: m.role,
        content: m.content
      }));

      const response = await fetch(CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`
        },
        body: JSON.stringify({
          messages: messagesToSend,
          context: {
            page: window.location.pathname
          },
          conversationState
        })
      });

      if (response.status === 429) {
        throw new Error("rate_limit");
      }

      if (response.status === 402) {
        throw new Error("payment_required");
      }

      if (!response.ok) {
        throw new Error("api_error");
      }

      const data = await response.json();
      
      if (data.reply) {
        setMessages(prev => [...prev, { role: "assistant", content: data.reply }]);
        
        // Update conversation state from backend
        if (data.conversationState) {
          setConversationState(data.conversationState);
        }

        // Show toast for successful actions
        if (data.actions?.includes("create_lead")) {
          toast({
            title: "✅ Lead registrado",
            description: "Tus datos han sido guardados en nuestro CRM.",
          });
        }
        if (data.actions?.includes("schedule_meeting")) {
          toast({
            title: "📅 Reunión agendada",
            description: "Un asesor te contactará para confirmar.",
          });
        }
      } else {
        throw new Error("no_reply");
      }
    } catch (error) {
      console.error("Chat error:", error);
      
      let errorMessage = "Estoy con alta demanda, intenta nuevamente en unos segundos.";
      
      if (error instanceof Error) {
        if (error.message === "rate_limit") {
          errorMessage = "Estoy atendiendo muchas consultas. Por favor, espera unos segundos e intenta de nuevo.";
        } else if (error.message === "payment_required") {
          errorMessage = "El servicio de chat no está disponible temporalmente.";
        }
      }

      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: errorMessage 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className={cn(
      "fixed bottom-24 right-5 z-50",
      "w-[360px] sm:w-[400px] h-[560px]",
      "bg-background border border-border",
      "rounded-2xl shadow-2xl",
      "flex flex-col overflow-hidden",
      "transition-all duration-300 ease-out",
      isOpen 
        ? "opacity-100 translate-y-0 pointer-events-auto" 
        : "opacity-0 translate-y-4 pointer-events-none"
    )}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-primary text-primary-foreground">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary-foreground/20 flex items-center justify-center">
            <span className="text-lg">🏠</span>
          </div>
          <div>
            <h3 className="font-semibold text-sm">Florencia</h3>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-xs opacity-90">
                {conversationState.leadCreated ? "Lead registrado" : "En línea"}
              </span>
            </div>
          </div>
        </div>
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={onClose}
          className="text-primary-foreground hover:bg-primary-foreground/20 h-8 w-8"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Status bar if actions completed */}
      {(conversationState.leadCreated || conversationState.meetingScheduled) && (
        <div className="px-4 py-2 bg-muted/50 border-b flex items-center gap-2 text-xs">
          {conversationState.leadCreated && (
            <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
              ✓ Lead guardado
            </span>
          )}
          {conversationState.meetingScheduled && (
            <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
              ✓ Reunión agendada
            </span>
          )}
        </div>
      )}

      {/* Messages */}
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        <div className="space-y-1">
          {messages.map((message, index) => (
            <ChatMessage key={index} role={message.role} content={message.content} />
          ))}
          {isLoading && <ChatTypingIndicator />}
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="p-3 border-t bg-muted/30">
        <div className="flex items-end gap-2">
          <Textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Escribe tu mensaje..."
            className="min-h-[44px] max-h-[120px] resize-none text-sm"
            disabled={isLoading}
            rows={1}
          />
          <Button 
            onClick={sendMessage} 
            disabled={!input.trim() || isLoading}
            size="icon"
            className="h-11 w-11 shrink-0"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground text-center mt-2">
          Shift+Enter para nueva línea
        </p>
      </div>
    </div>
  );
};
